import { Zap, AlertTriangle } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";

export interface MaterialOption {
  key: string;
  label: string;
  pricePerSq: number;
}

interface PricingControlsProps {
  pricePerSq: number;
  onPricePerSqChange: (v: number) => void;
  material: string;
  onMaterialChange: (v: string) => void;
  materials: MaterialOption[];
  wastePercent: number;
  onWasteChange: (v: number) => void;
  complexityPercent: number;
  onComplexityChange: (v: number) => void;
}

export function PricingControls({
  pricePerSq, onPricePerSqChange,
  material, onMaterialChange, materials,
  wastePercent, onWasteChange,
  complexityPercent, onComplexityChange,
}: PricingControlsProps) {
  return (
    <div className="space-y-5 min-w-0">
      <div className="rounded-xl border border-border bg-muted/30 p-4 space-y-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <Label className="flex items-center gap-2 font-semibold">
            <Zap className="w-4 h-4 text-primary" /> Your Price Per Square
          </Label>
          <div className="flex w-full items-center justify-between gap-2 sm:w-auto sm:justify-end">
            <span className="text-sm text-muted-foreground">$</span>
            <Input
              type="number"
              value={pricePerSq}
              onChange={(e) => onPricePerSqChange(Number(e.target.value))}
              className="h-8 w-full max-w-[120px] text-right font-bold [appearance:textfield] sm:w-20 [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-sm text-muted-foreground">/SQ</span>
          </div>
        </div>
        <Slider
          value={[pricePerSq]}
          min={200}
          max={1200}
          step={10}
          onValueChange={(v) => onPricePerSqChange(v[0])}
        />
        <div className="grid grid-cols-3 gap-2 text-[10px] text-muted-foreground">
          <span className="text-left">$200 (Budget)</span>
          <span className="text-center">$650 (Average)</span>
          <span className="text-right">$1,200 (Premium)</span>
        </div>
        <p className="text-xs text-muted-foreground">
          This is what you charge the homeowner per roofing square. It overrides the itemized cost breakdown below.
        </p>
      </div>

      <div className="space-y-2">
        <Label>Roofing Material</Label>
        <Select value={material} onValueChange={onMaterialChange}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {materials.map((m) => (
              <SelectItem key={m.key} value={m.key}>
                {m.label} <span className="text-muted-foreground ml-2">${m.pricePerSq}/SQ</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <Label className="flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5 text-amber-500" /> Waste Factor
          </Label>
          <span className="text-sm font-bold text-amber-600">{wastePercent}%</span>
        </div>
        <Slider value={[wastePercent]} min={5} max={25} step={1} onValueChange={(v) => onWasteChange(v[0])} />
        <div className="flex items-start justify-between gap-4 text-[10px] text-muted-foreground">
          <span className="max-w-[45%]">5% (Simple roof)</span>
          <span className="max-w-[45%] text-right">25% (Complex cuts)</span>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
          <Label>Complexity / Steep Adder</Label>
          <span className="text-sm font-bold">{complexityPercent}%</span>
        </div>
        <Slider value={[complexityPercent]} min={0} max={30} step={1} onValueChange={(v) => onComplexityChange(v[0])} />
        <div className="flex items-start justify-between gap-4 text-[10px] text-muted-foreground">
          <span className="max-w-[45%]">0% (Easy access)</span>
          <span className="max-w-[45%] text-right">30% (Steep/Multi-story)</span>
        </div>
      </div>
    </div>
  );
}
